import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, ExternalLink, BarChart3, Trash2, Edit, Eye, Plus, Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface HostedSite {
  id: string;
  slug: string;
  site_name: string;
  description: string | null;
  views_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  edit_token: string;
}

const MySites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState<HostedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [analyticsDialog, setAnalyticsDialog] = useState<{ open: boolean; siteId: string; token: string } | null>(null);
  const [editTokenInput, setEditTokenInput] = useState('');
  const [showTokenDialog, setShowTokenDialog] = useState(false);

  useEffect(() => {
    if (user) fetchSites();
    else setLoading(false);
  }, [user]);

  const fetchSites = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('hosted_sites')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setSites((data as HostedSite[]) || []);
    } catch (e: any) {
      console.error('Error fetching sites:', e);
      toast.error('Failed to load your sites');
    } finally {
      setLoading(false);
    }
  };

  const deleteSite = async (editToken: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('publish-site', {
        method: 'DELETE',
        body: { editToken },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('Site deleted');
      fetchSites();
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete site');
    }
  };

  const openAnalytics = (siteId: string, token: string) => {
    window.open(`/site-analytics?siteId=${siteId}&token=${token}`, '_blank');
  };

  const filteredSites = sites.filter(s =>
    s.site_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-accent/20 p-6 border border-primary/20">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 shadow-lg shadow-primary/20">
              <Globe className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">My Hosted Sites</h1>
              <p className="text-muted-foreground text-sm mt-1">Manage your published websites</p>
            </div>
          </div>
          <Button onClick={() => navigate('/dashboard/tools/code-studio')} className="gap-2">
            <Plus className="h-4 w-4" />
            Create New
          </Button>
        </div>
      </div>

      {/* Search */}
      {sites.length > 0 && (
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {/* Sites Grid */}
      {filteredSites.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Globe className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {sites.length === 0 ? 'No hosted sites yet' : 'No matching sites'}
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              {sites.length === 0
                ? 'Create a project in Code Studio and publish it to the web!'
                : 'Try a different search term'}
            </p>
            {sites.length === 0 && (
              <Button variant="outline" onClick={() => navigate('/dashboard/tools/code-studio')} className="gap-2">
                <Plus className="h-4 w-4" />
                Open Code Studio
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSites.map((site) => (
            <Card key={site.id} className="group hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-base">{site.site_name}</CardTitle>
                    <CardDescription className="font-mono text-xs">
                      /s/{site.slug}
                    </CardDescription>
                  </div>
                  <Badge
                    variant={site.is_published ? 'default' : 'secondary'}
                    className="text-[10px]"
                  >
                    {site.is_published ? 'Live' : 'Draft'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    {site.views_count.toLocaleString()} views
                  </span>
                  <span className="text-xs">
                    {format(new Date(site.updated_at), 'MMM d, yyyy')}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1 text-xs"
                    onClick={() => window.open(`/s/${site.slug}`, '_blank')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Visit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1 text-xs"
                    onClick={() => navigate('/dashboard/tools/code-studio', { state: { editToken: site.edit_token, siteSlug: site.slug, siteName: site.site_name } })}
                  >
                    <Edit className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1 text-xs"
                    onClick={() => openAnalytics(site.id, site.edit_token)}
                  >
                    <BarChart3 className="h-3.5 w-3.5" />
                    Analytics
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {site.site_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete your site and all its analytics data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteSite(site.edit_token)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MySites;
